import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { dateDiff } from "~/helpers/date";
import { WorkflowListSteps } from "~/molecules/WorkflowListSteps";
import { Link } from "react-router";
import { type WorkflowListItem } from "~/api/types";
import SimpleDataTable, { type TableColumn } from "~/molecules/SimpleDataTable";
import { DueDateCell } from "~/molecules/DueDateCell";
import {
  useWorkflows,
  QueryParams as WorkflowQueryParams,
} from "~/api/v2/useWorkflows";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { Label } from "~/atoms/label";
import { Switch } from "~/atoms/switch";
import { FilterPopover } from "~/molecules/FilterPopover";
import SearchBar from "~/molecules/SearchBar";
import { useFilter, type Filter } from "~/hooks/useFilter";
import { useEnhancedFilters } from "~/helpers/legalEntities";
import { useTags } from "~/api/swr/useTags";
import { useTiers } from "~/api/swr/useTiers";
import { useCategories } from "~/api/swr/useCategories";
import { extractOwners } from "~/helpers/workflow";
import DocumentIcon from "~/molecules/DocumentIcon";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { useUserContext } from "~/hooks/useUserContext";
import { tabs } from "~/pages/WorkflowsPage";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";
import { MyWorkflowStepsCell } from "~/molecules/MyWorkflowStepsCell";

export const WorkflowList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allWorkflows, isLoading } = useWorkflows({
    titleSearch: searchParams.get(WorkflowQueryParams.Title),
  });
  const activeTab = searchParams.get("tab") ?? tabs.ongoing;

  const workflows = allWorkflows?.filter((w) =>
    activeTab === tabs.ongoing ? w.isOngoing : !w.isOngoing,
  );

  const { data: tags } = useTags();
  const { data: tiers } = useTiers();
  const { data: categories } = useCategories();
  const { t } = useTranslation();
  const { user } = useUserContext();
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);

  const [expandAll, setExpandAll] = useState(false);

  const baseFilterInput: Filter<WorkflowListItem>[] =
    categories && tiers && workflows && tags
      ? [
          {
            id: "category",
            label: t(($) => $.library.categoryView),
            options: categories.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (w) => `${w.document.category.id}`,
          },
          {
            id: "owner",
            label: t(($) => $.library.filter.owner),
            options: extractOwners(
              workflows.map((w) => w.document.owner),
              user.id,
            ),
            getValue: (w) => `${w.document.owner.userId}`,
          },
          {
            id: "tag",
            label: t(($) => $.library.filter.tags),
            options: tags.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (w) => w.document.tagIds.map((id) => `${id}`),
          },
          {
            id: "tier",
            label: t(($) => $.library.filter.tiers),
            options: tiers.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (w) => `${w.document.tier.id}`,
          },
        ]
      : [];

  const filterInput = useEnhancedFilters(baseFilterInput, (w) => w.document.id);

  const {
    filters,
    activeFilters,
    handleClearFilters,
    handleSelectFilter,
    filterItems,
  } = useFilter(filterInput);

  const filteredWorkflows = useMemo(
    () => filterItems(workflows ?? []),
    [workflows, filterItems],
  );

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!allWorkflows?.length && !searchParams.get(WorkflowQueryParams.Title)) {
    return <NoResult />;
  }

  const allColumns: TableColumn<WorkflowListItem>[] = [
    ...(isRefNumEnabled
      ? [
          {
            columnKey: "commons.document.referenceNumber",
            header: t(($) => $.commons.document.referenceNumber),
            compareFn: (a: WorkflowListItem, b: WorkflowListItem) =>
              (a.document.referenceNumber ?? "").localeCompare(
                b.document.referenceNumber ?? "",
              ),
            cell: (i: WorkflowListItem) => (
              <span>{i.document.referenceNumber ?? "-"}</span>
            ),
          },
        ]
      : []),
    {
      columnKey: "commons.title",
      header: t(($) => $.commons.title),
      compareFn: (a, b) =>
        a.documentVersion.title.localeCompare(b.documentVersion.title),
      cell: (i) => (
        <Link
          className="flex items-center gap-1 hover:underline"
          to={`/documents/${i.document.id}?versionId=${i.documentVersion.id}&tab=workflow`}
        >
          <DocumentIcon
            type={i.documentVersion.fileType}
            className="size-5 shrink-0"
          />
          {i.documentVersion.title}
        </Link>
      ),
    },
    {
      columnKey: "commons.workflow.mySteps",
      header: t(($) => $.commons.workflow.mySteps),
      compareFn: (a, b) => {
        const aHasParticipant = a.steps.some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );
        const bHasParticipant = b.steps.some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );

        if (aHasParticipant === bHasParticipant) return 0;
        return aHasParticipant ? -1 : 1;
      },
      cell: (workflow) => (
        <MyWorkflowStepsCell steps={workflow.steps} currentUserId={user.id} />
      ),
    },
    {
      columnKey: "commons.workflow.progress",
      header: t(($) => $.commons.workflow.progress),
      compareFn: (a, b) =>
        a.completedSteps / a.totalSteps - b.completedSteps / b.totalSteps,
      cell: (wf) => (
        <WorkflowProgress
          completedSteps={wf.completedSteps}
          totalSteps={wf.totalSteps}
        />
      ),
    },
    {
      columnKey: "commons.document.reviewFrom",
      header: t(($) => $.commons.document.reviewFrom),
      compareFn: (a, b) =>
        dateDiff(a.documentVersion.reviewFrom, b.documentVersion.reviewFrom),
      cell: (wf) => <DueDateCell dueDate={wf.documentVersion.reviewFrom} />,
    },
    {
      columnKey: "commons.document.reviewTo",
      header: t(($) => $.commons.document.reviewTo),
      compareFn: (a, b) =>
        dateDiff(a.documentVersion.reviewTo, b.documentVersion.reviewTo),
      cell: (wf) => <DueDateCell dueDate={wf.documentVersion.reviewTo} />,
    },
    {
      columnKey: "commons.document.owner",
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) =>
        a.document.owner.firstName.localeCompare(b.document.owner.firstName),
      cell: (wf) => (
        <span>
          {wf.document.owner.firstName} {wf.document.owner.lastName}
        </span>
      ),
    },
    {
      columnKey: "commons.document.category",
      header: t(($) => $.commons.document.category),
      compareFn: (a, b) =>
        a.document.category.title.localeCompare(b.document.category.title),
      cell: (wf) => <span>{wf.document.category.title}</span>,
    },
    {
      columnKey: "commons.document.tier",
      header: t(($) => $.commons.document.tier),
      compareFn: (a, b) =>
        a.document.tier.title.localeCompare(b.document.tier.title),
      cell: (wf) => <span>{wf.document.tier.title}</span>,
    },
  ];

  const columns = (
    activeTab === tabs.ongoing
      ? allColumns.filter((c) => c.columnKey !== "commons.document.reviewFrom")
      : allColumns.filter((c) => c.columnKey !== "commons.workflow.progress")
  ).map((c) => ({
    ...c,
    defaultSort: c.columnKey === "commons.workflow.mySteps",
  }));

  const renderData = () => {
    if (!filteredWorkflows?.length) {
      return <NoResult />;
    }

    return (
      <div className="border-border border">
        <SimpleDataTable
          columns={columns}
          data={filteredWorkflows}
          renderExpandedRow={(w) => <WorkflowListSteps steps={w.steps} />}
          enablePinning={true}
          expandAll={expandAll}
          storageKey="workflows"
        />
      </div>
    );
  };

  const setTitleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(WorkflowQueryParams.Title, value);
    } else {
      params.delete(WorkflowQueryParams.Title);
    }
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <FilterPopover
          filters={filters}
          activeFiltersCount={activeFilters.length}
          onClearFilters={handleClearFilters}
          onSelectFilter={handleSelectFilter}
        />
        <div className="flex items-center gap-4 rounded-md border px-2">
          <Label>{t(($) => $.commons.workflow.expandAll)}</Label>
          <Switch checked={expandAll} onCheckedChange={setExpandAll} />
        </div>
        <SearchBar
          placeholder={t(($) => $.commons.workflows.search)}
          value={searchParams.get(WorkflowQueryParams.Title) ?? ""}
          onDebouncedSearch={setTitleSearch}
          onSearch={setTitleSearch}
          onClearSearch={() => setTitleSearch("")}
        />
      </div>
      {renderData()}
    </div>
  );
};
